import {useEffect} from 'react';
import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {getSetting} from '@woocommerce/settings';
import {decodeEntities} from '@wordpress/html-entities';
import {loadStripe} from '../stripe';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {startTransaction, updateTransaction, z} from '@peachpay/checkout-blocks';
import CurrencyFallback from '../../../../../blocks/src/currency-fallback';
import {parsePaymentData} from '../stripe';
import {getErrorMessage} from '../../../../../../frontend/@shared/ts/error';
import {type PaymentRequestPaymentMethodEvent} from '@stripe/stripe-js';

const SettingsSchema = z.object({
	connect_id: z.string(),
	currency_fallback_required: z.string().nullable(),
	description: z.string(),
	icon_url: z.string(),
	public_key: z.string(),
	setup_intent_nonce: z.string(),
	setup_intent_url: z.string(),
	supports: z.string().array(),
	title: z.string(),
});

const schemaResult = SettingsSchema.safeParse(getSetting('peachpay_stripe_googlepay_data', false));

if (!schemaResult.success) {
	throw new Error(
		`PeachPay Stripe GooglePay data is not available \n\n${schemaResult.error.message}`,
	);
}

const method_data = schemaResult.data;

const title = decodeEntities(method_data.title);
const description = decodeEntities(method_data.description);

loadStripe(method_data.public_key, method_data.connect_id)
	.then(stripe => {
		if (stripe === null) {
			throw new Error(__('Stripe is null', 'peachpay-for-woocommerce'));
		}

		if (typeof stripe === 'string') {
			throw new Error(stripe);
		}

		/**
     * Content component
     */
		const Content = (context: PaymentMethodInterface) => {
			const {emitResponse, eventRegistration, billing} = context;
			const {onPaymentSetup, onCheckoutSuccess, onCheckoutFail} = eventRegistration;

			useEffect(() => {
				const unsubscribe = onPaymentSetup(async () => {
					try {
						const start_transaction = await startTransaction('peachpay_stripe_googlepay');

						if (start_transaction.error) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: getErrorMessage(start_transaction.error),
							};
						}

						if (start_transaction.result === undefined) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: __('Missing transaction ID', 'peachpay-for-woocommerce'),
							};
						}

						const transaction_ID = start_transaction.result;

						const googlepay = stripe.paymentRequest({
							country: billing.billingAddress.country,
							currency: (billing.currency.code).toLowerCase(),
							total: {
								amount: billing.cartTotal.value,
								label: billing.cartTotal.label,
							},
							disableWallets: ['applePay', 'browserCard', 'link'],
						});

						const result = await googlepay.canMakePayment();

						if (!result?.['googlePay']) {
							const note = __('Failed to create payment method', 'peachpay-for-woocommerce');
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						googlepay.update({
							currency: (billing.currency.code).toLowerCase(),
							total: {
								label: billing.cartTotal.label,
								amount: billing.cartTotal.value,
							},
							displayItems: billing.cartTotalItems.map(item => ({
								label: item.label,
								amount: item.value,
							})),
						});

						const [paymentMethodId, error]: [string | null, string] = await new Promise(resolve => {
							googlepay.once('paymentmethod', (event: PaymentRequestPaymentMethodEvent) => {
								googlepay.off('cancel');
								event.complete('success');
								resolve([event.paymentMethod.id, '']);
							});

							googlepay.once('cancel', () => {
								googlepay.off('paymentmethod');

								resolve([null, 'Payment was canceled by customer']);
							});

							googlepay.show();
						});

						if (!paymentMethodId) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: error,
							};
						}

						return {
							type: emitResponse.responseTypes.SUCCESS,
							meta: {
								paymentMethodData: {
									peachpay_transaction_id: transaction_ID,
									peachpay_stripe_payment_method_id: paymentMethodId,
								},
							},
						};
					} catch (error) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: getErrorMessage(error),
						};
					}
				});

				return unsubscribe;
			}, [billing.billingAddress, onPaymentSetup]);

			useEffect(() => {
				const unsubscribe = onCheckoutSuccess(async (data: {redirectUrl: string}) => {
					const dataURL = new URL(data.redirectUrl);

					const [name, encodedStringData] = dataURL.hash.split('=');

					if (dataURL.hash === '' || name !== '#payment_data' || !encodedStringData) {
						return;
					}

					const paymentData = parsePaymentData(encodedStringData);

					if (paymentData === null) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: __('Failed to parse payment data', 'peachpay-for-woocommerce'),
						};
					}

					const transaction_ID = paymentData.transaction_id;
					const clientSecret = paymentData.data.client_secret;
					const intermediateURL = paymentData.intermediate_url;
					const successURL = paymentData.success_url;

					const {paymentIntent: confirmIntent, error: confirmationError}
            = await stripe.confirmCardPayment(clientSecret, {return_url: intermediateURL});

					if (confirmationError ?? !confirmIntent) {
						const message
              = confirmationError?.message
              ?? __('Failed to confirm payment intent.', 'peachpay-for-woocommerce');

						await updateTransaction(transaction_ID, {note: message});
						return {
							type: emitResponse.responseTypes.ERROR,
							message,
							messageContext: emitResponse.noticeContexts.PAYMENTS,
						};
					}

					if (confirmIntent.status === 'succeeded') {
						return {
							type: emitResponse.responseTypes.SUCCESS,
							redirectUrl: successURL,
						};
					}

					const message = __('Payment intent missing', 'peachpay-for-woocommerce');
					await updateTransaction(transaction_ID, {note: message});
					return {
						type: emitResponse.responseTypes.ERROR,
						message,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				});

				return unsubscribe;
			}, [onCheckoutSuccess]);

			useEffect(() => {
				const unsubscribe = onCheckoutFail((data: CheckoutAfterProcessingWithErrorEventData) => {
					if (data.processingResponse?.paymentStatus === emitResponse.responseTypes.FAIL) {
						return {
							type: emitResponse.responseTypes.FAIL,
							message: data.processingResponse.paymentDetails['message'],
							messageContext: emitResponse.noticeContexts.PAYMENTS,
						};
					}

					return {
						type: emitResponse.responseTypes.FAIL,
						message:
              data.processingResponse?.message
              ?? __('Checkout failed unexpectedly', 'peachpay-for-woocommerce')
                + `${JSON.stringify(data)}`,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				});

				return unsubscribe;
			}, [onCheckoutFail]);

			if (method_data.currency_fallback_required) {
				return <CurrencyFallback name={title} currency={method_data.currency_fallback_required} />;
			}

			return (
				<>
					<img style={{maxHeight: '25px'}} src={method_data.icon_url} />
					<p style={{textAlign: 'left', margin: '0.5rem 0 0'}}>{description}</p>
				</>
			);
		};

		/**
     * Label component
     *
     * @param {PaymentMethodInterface} props Props from payment API.
     */
		const Label = (props: PaymentMethodInterface) => {
			const {PaymentMethodLabel, PaymentMethodIcons} = props.components;
			return (
				<>
					<PaymentMethodLabel text={title} />
					<PaymentMethodIcons
						className='pp-label-icon'
						icons={[
							{
								id: 'peachpay_stripe_googlepay',
								src: method_data.icon_url,
								alt: title,
							},
						]}
					/>
				</>
			);
		};

		registerPaymentMethod({
			ariaLabel: title,
			canMakePayment(data) {
				return data.paymentMethods.includes('peachpay_stripe_googlepay');
			},
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			content: <Content />,
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			edit: <Content />,
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			label: <Label />,
			name: 'peachpay_stripe_googlepay',
			supports: {
				features: method_data.supports,
				showSavedCards: false,
				showSaveOption: false,
			},
		});
	})
	.catch(error => {
		throw new Error(getErrorMessage(error));
	});
